/**
 * The pure half of Taskize: row keys, title normalisation, and the "this is already in the
 * document" check.
 *
 * All of it is client-side, and it stays pure so it can be tested without a tray, a document
 * or a model. kms computed the same things inside a MobX store, where none of it was reachable
 * from a test.
 */

/** Rows at or above this arrive checked; below it they arrive unchecked, wearing a "low" chip. */
export const CONFIDENCE_PRECHECK = 0.7;

/**
 * djb2, base-36 — the same hash kms used, with one correction.
 *
 * kms fed it `partId + normalizeTitle(title)`, and `partId` is **never sent** (W7 §0 C6), so in
 * practice the key was the normalized title alone and **two rows with the same title collapsed
 * into one** (§E6.10). The row's index goes into the key here, which costs nothing and makes
 * two genuinely-repeated action items two rows.
 */
export function hashKey(...parts: Array<string | number>): string {
  let hash = 5381;
  const input = parts.join(" ");
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 33) ^ input.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
}

/** Case, punctuation and whitespace are noise when asking "is this the same item?". */
export function normalizeTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[`*_~]/g, "")
    .replace(/[.,;:!?]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const CHECKLIST_RE = /^\s*[-*]\s+\[[ xX]\]\s+(.+)$/gm;
const DECISION_RE = /^\s*>\s*📌\s*\*\*Decision:\*\*\s*(.+)$/gm;

/**
 * Everything this document already records, scraped from its markdown.
 *
 * The committed meta suffix is stripped — a task line reads `- [ ] Ship it _(Dana · by Friday)_`
 * and a decision reads `> 📌 **Decision:** We ship first — decided · 07-08` — so a re-run's
 * clean title still matches what a previous confirm wrote. Without that, Taskize would offer
 * the same five rows every single time it was asked.
 */
export function existingTitles(markdown: string): Set<string> {
  const found = new Set<string>();
  const collect = (pattern: RegExp) => {
    pattern.lastIndex = 0;
    let match = pattern.exec(markdown);
    while (match) {
      const raw = match[1] ?? "";
      found.add(normalizeTitle(stripCommittedMeta(raw)));
      match = pattern.exec(markdown);
    }
  };
  collect(CHECKLIST_RE);
  collect(DECISION_RE);
  found.delete("");
  return found;
}

/** ` _(Dana · by Friday)_` and ` — decided · 07-08` are ours, not the person's words. */
export function stripCommittedMeta(title: string): string {
  return title
    .replace(/\s*_\([^)]*\)_\s*$/u, "")
    .replace(/\s+—\s+[^—]*$/u, "")
    .trim();
}

/** Drop anything the document already says. Reminders are never deduped — they are moments. */
export function dropCommitted<T extends { title: string }>(rows: T[], existing: Set<string>): T[] {
  return rows.filter((row) => !existing.has(normalizeTitle(row.title)));
}
